<template>
  <div>
    <div class="mx-auto" style="max-width:700px;">
      <v-alert
          v-if="share && !isShared && !isLoading"
          rounded
          type="error"
          outlined
          prominent
          class="back_accent"
      >
        {{ $t('dialog.share_result.invalid') }}
      </v-alert>

      <div v-else-if="result">
        <div class="d-flex justify-end align-center d-print-none">
          <Breadcrumbs v-if="!share" :items="bread"></Breadcrumbs>
          <v-spacer></v-spacer>
          <v-btn v-if="!trial" @click="toggleShowAll" small elevation="0" class="ml-1">
            <v-icon v-if="showAll" color="green" left small>mdi-toggle-switch</v-icon>
            <v-icon v-else left small>mdi-toggle-switch-off</v-icon>
            {{ $t("result.detail_view") }}
          </v-btn>
          <v-btn v-if="result" @click="printResult()" small elevation="0" class="ml-1">
            <v-icon class="mr-1" small>mdi-printer</v-icon>
          </v-btn>
        </div>
        <div v-if="result" class="text-center mt-1">
          <span class="subtitle-1" style="line-height:0.7rem;">
          <span v-if="getFullName"><strong>{{getFullName}}</strong>, </span>
          </span>
            <br>
            <span class="caption" style="line-height:0.4rem;">
          <span v-if="getGender"><v-icon class="pb-1" small>{{getGender}}</v-icon></span>
          <span v-if="getAge">{{getAge}} {{ $t('result.years') }}, </span>
          <span v-if="result.completed_date">{{ result.completed_date|moment("l LT") }}</span>
          </span>
        </div>
      </div>
      <v-skeleton-loader
          v-if="isLoading"
          ref="skeleton"
          :type="'table'"
          class="mx-auto"
          :elevation="this.$globals.elevation_skeleton"
      ></v-skeleton-loader>
      <div v-else-if="result && !(share && !isShared)" class="mt-3">
        <v-alert
            rounded
            v-if="result.parameters.influence >=21"
            text
            type="error">
          <v-row align="center">
            <v-col class="grow py-0">
              {{ $t("result.influence2") }}
            </v-col>
          </v-row>
        </v-alert>
        <v-alert
            rounded
            v-if="result.parameters.influence === 20"
            text
            type="warning">
          <v-row align="center">
            <v-col class="grow py-0">
              {{ $t("result.influence1") }}
            </v-col>
          </v-row>
        </v-alert>

        <!-- TRIAL -->
        <div v-if="showThis && trial && trialData && (trial && !fullTrial)">
          <div class="mb-5">
            <v-card-title class="font-weight-bold pt-2 pb-1 d-flex justify-center" style="text-transform: uppercase;">
              {{ $t('result.params.title') }}
            </v-card-title>
            <v-card rounded outlined>
              <div class="d-flex justify-center my-3">
                <v-progress-circular
                    :rotate="270"
                    :size="200"
                    :width="15"
                    :value="trialData.attitudes.balancep"
                    :color="colors.green"
                >
                  <strong class="text--primary">{{ trialData.attitudes.balancep }}%</strong>
                </v-progress-circular>
              </div>
              <p class="text-center" v-html="$t('result.trial_balance', [trialData.attitudes.balance, trialData.attitudes.total])">
              </p>
            </v-card>
          </div>
        </div>

        <!-- PARAMETERS -->
        <div v-if="showThis && !trial || (trial && fullTrial)">
          <div v-if="!share || (isShared && sharedOptions.params)">
            <v-card-title class="font-weight-bold pt-2 pb-1 d-flex justify-center" style="text-transform: uppercase;">
              {{ $t('result.params.title') }}
            </v-card-title>
            <div v-for="(params,index,key) in result.parameters.bmFull.groups" :key="'p-'+index"
                 :class="{'mb-2':true,'bm-page-break':(key>0 && showAll) || (key>0 && key %2 ===0 && !showAll)}">
              <param-slot
                  :param_key="index"
                  :params="params"
              ></param-slot>
            </div>
          </div>
        </div>

        <!-- ROLES -->
        <div v-if="!trial && result.parameters.roles" class="bm-page-break">
          <div v-if="!share || (isShared && sharedOptions.roles)">
            <v-card-title class="font-weight-bold pb-1 d-flex justify-center" style="text-transform: uppercase;">
              {{ $t('result.roles.title') }}
            </v-card-title>
            <div class="mb-2">
              <roles
                  :roles = result.parameters.roles
              ></roles>
            </div>
          </div>
        </div>

        <!-- MOTIVATION -->
        <div v-if="showThis && !trial || (trial && fullTrial)" :class="{'bm-page-break':showAll}">
          <div v-if="!share || (isShared && sharedOptions.motivation)">
            <v-card-title class="font-weight-bold pb-1 d-flex justify-center" style="text-transform: uppercase;">
              {{ $t('result.motivation.title') }}
            </v-card-title>
            <div class="mb-2">
              <motivation
                  :regime="result.parameters.bmFull.regime.score"
                  :approach="result.parameters.bmFull.approach"
                  :energy="result.parameters.bmFull.energy"
              ></motivation>
            </div>
          </div>
        </div>

        <!-- MOTIVATORS -->
        <div v-if="showThis">
          <motivators
              v-if="!share || (isShared && sharedOptions.motivation)"
              :class="{'mt-5 bm-page-break': !trial}"
              :result="result"
              :result_type="result_type"
          ></motivators>
        </div>


        <div v-if="trial && !share && showTrialAction && !fullTrial">
            <v-alert
                rounded
                type="info"
                outlined
                prominent
                class="back_accent"
            >
              <v-row align="center">
                <v-col class="grow">
                  {{ $t("result.trial_text")}}
                </v-col>
                <v-col class="shrink">
                  <v-btn dark color="blue" @click="trialAction" :loading="trialLoading">
                    {{ $t("result.trial_button_action") }}</v-btn>
                </v-col>
              </v-row>
            </v-alert>
        </div>

        <v-dialog
            v-model="showTrialDialog"
            max-width="400"
        >
          <v-card>
            <v-card-title class="text-h5">
              <v-icon class="mr-1" color="green darken-1">mdi-check</v-icon>{{ $t("result.trial_dialog_title") }}
            </v-card-title>
            <v-card-text>{{ $t("result.trial_dialog_text") }}</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                  color="green darken-1"
                  dark
                  @click="showTrialDialog = false"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>

        </v-dialog>

      </div>
    </div>

  </div>

</template>

<script>
import Breadcrumbs from "../components/Breadcrumbs";
export default {
  name: "Result",
  components: {
    Breadcrumbs,
    ParamSlot: () => import('../components/ParamSlot'),
    Motivation: () => import('../components/Motivation'),
    Motivators: () => import('../components/Motivators'),
    ShareResult: () => import('../components/ShareResult'),
    Roles: () => import('../components/Roles')
  },
  props: ['trial'],
  metaInfo: {
    title: 'Balance Management',
  },
  data() {
    return {
      trialLoading: false,
      showTrialAction: true,
      showTrialDialog: false,
      showThis: true, //zatim dev, skryt kdyz nechci
      bread: [
        {
          text: '',
          disabled: true,
          to: null,
        },
        {
          text: this.$t('result.bread_result'),
          disabled: true,
          to: null,
        }
      ]
    }
  },
  computed: {
    colors() {
      return this.$store.getters['app/colors']
    },
    fullTrial() {
      return this.$store.getters['auth/trialFull'];
    },
    trialData() {
      if (this.result && this.result.parameters && this.result.parameters.bmFull) {
        // console.log(this.result);
        const groups = this.result.parameters.bmFull.groups;
        let balCount = 0;
        let disbalCount = 0;
        let total = 0;
        for (let group in groups) {
          const params = groups[group].parameters;
          for (let param in params) {
            const score = params[param];
            if (score >= 3 && score < 8) {
              balCount += 1;
            } else {
              disbalCount += 1;
            }
            total += 1;
          }
        }
        return {
          attitudes: {
            total,
            balance: balCount,
            balancep: Math.round(balCount / total * 1000) / 10,
            disbalance: disbalCount,
            disbalancep: Math.round(disbalCount / total * 1000) / 10,
          }
        }
      }
      return null;
    },
    share() {
      return this.$router.currentRoute.name === 'share'
    },
    isShared() {
      return this.$store.getters['diagnostic/isShared']
    },
    sharedOptions() {
      return this.$store.getters['diagnostic/shareOptions']
    },
    result_type() {
      // TODO BC / trial result type default to 1
      return this.result.custom_data.result_type ? this.result.custom_data.result_type : 1
    },
    result() {
      let result = this.$store.getters['diagnostic/result']
      if (result) {
          // console.log(result)
        if (this.trial) {
          this.bread[0].text = 'Trial'
          this.bread[0].disabled = true
          this.bread[0].to = null
        } else {
          this.bread[0].disabled = false
        }
      }
      //console.log(result.parameters) //smazat
      // console.log(result.parameters.bmFull.groups)
      return result
    },
    getFullName() {
      let name = ""
      if (this.result && !this.trial) {
        if (this.result.custom_data.firstname) {
          name = this.result.custom_data.firstname
        }
        if (this.result.custom_data.lastname) {
          name = name + " " + this.result.custom_data.lastname
        }
      }
      return name
    },
    getGender() {
      let gender = null
      gender = this.result.custom_data.gender
      if (gender) {
        return this.genders.find(x=>x.id === gender).icon
      } else {
        return gender
      }
    },
    getAge() {
      let age = null
      age = this.result.custom_data.age
      if (age) {
        return this.ages[age]
      } else {
        return age
      }
    },
    motivation() {
      return this.$store.getters['diagnostic/motivation']
    },
    genders() {
      return this.$store.getters['diagnostic/genders']
    },
    ages() {
      return this.$store.getters['diagnostic/ages']
    },
    affected() {
      return this.$store.getters['diagnostic/affected']
    },
    showAll(){
      return this.$store.getters['diagnostic/showAll']
    },
    isLoading() {
      return this.$store.getters['diagnostic/isLoading']
    },
  },
  methods: {
    toggleShowAll(){
      this.$store.dispatch('diagnostic/showAllToggle')
    },
    printResult() {
      window.print();
    },
  },
  created() {
    if (!this.trial) {
      let slug = this.$route.params.id
      this.slug = slug
      this.$store.dispatch('diagnostic/loadResult', {"slug": slug})
    }
  }
}
</script>
